import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { LoginService } from '@app/shared/services/login.service';
import { DiscrepanciesService } from 'api/services';
import { AuthService } from '@app/core/services/auth.service';
import { RegisterOrganizationComponent } from '@app/core/components/dialogs/register-organization/register-organization.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { TermsOfServiceNotAcceptedError } from '@app/shared/constants/http-context-tokens';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { UnauthFooterComponent } from '../../components/unauth-footer/unauth-footer.component';

@Component({
    selector: 'app-login-callback',
    templateUrl: './login-callback.component.html',
    styleUrls: ['./login-callback.component.scss'],
    imports: [
        MatProgressSpinner,
        MatButton,
        UnauthFooterComponent,
        TranslateModule,
    ],
})
export class LoginCallbackComponent implements OnInit {
    folderId: number;

    constructor(
        private sessionService: SessionService,
        private storageService: StorageService,
        private discrepanciesService: DiscrepanciesService,
        private router: Router,
        private authService: AuthService,
        private loginService: LoginService,
        private matDialog: MatDialog,
        private toastr: ToastService,
        private translate: TranslateService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.handleOAuthCallback();
    }

    async handleOAuthCallback(): Promise<void> {
        try {
            await this.authService.configureOAuthService();
        } catch (error) {
            await this.router.navigate(['/login']);
            return;
        }

        this.authService.login().subscribe(
            async result => {
                if (result) {
                    let route = '/';

                    // extract state
                    const state = this.authService.getOAuthState();
                    if (state) {
                        const parsed = this.authService.parseOAuthState(state);
                        if (parsed && parsed.r) {
                            route = parsed.r;
                        }
                    }

                    // check if authenticated. if not redirect to root application page
                    const isAuthenticated = this.authService.isAuthenticated();
                    if (isAuthenticated) {
                        // Load user data from backend API
                        try {
                            await this.sessionService.loadUserFromApi();
                        } catch (userDataError) {
                            // ApiInterceptor already navigated to /settings/tos
                            if (
                                userDataError instanceof
                                TermsOfServiceNotAcceptedError
                            ) {
                                return;
                            }
                            // The organization is not unlocked for c4l-addons.
                            // Warn and stay on /login so the user sees why they
                            // cannot proceed.
                            if (
                                this.sessionService.isOrganizationNotAllowedError(
                                    userDataError
                                )
                            ) {
                                this.toastr.warning(
                                    this.translate.instant(
                                        'ADDONS.ORGANIZATION_NOT_REGISTERED'
                                    )
                                );
                                await this.sessionService.logout(true);
                            }
                            await this.router.navigate(['/login']);
                            return;
                        }

                        // check if organization has registered in addons
                        if (!this.sessionService.hasRegisteredInAddons()) {
                            if (this.sessionService.isOrganizationAdmin) {
                                this.matDialog
                                    .open(RegisterOrganizationComponent, {
                                        width: '55vw',
                                        disableClose: true,
                                    })
                                    .afterClosed()
                                    .subscribe(async dialogResult => {
                                        if (dialogResult) {
                                            this.toastr.success(
                                                this.translate.instant(
                                                    'ADDONS.REGISTER_ORGANIZATION_DIALOG.ON_SUCCESS'
                                                )
                                            );

                                            await this.finishAuthenticationProcess(
                                                route
                                            );
                                        } else {
                                            await this.sessionService.logout();
                                        }
                                    });
                            } else {
                                await this.router.navigate(['/login']);
                                return;
                            }
                        } else {
                            await this.finishAuthenticationProcess(route);
                        }
                    } else {
                        await this.router.navigate(['/login']);
                    }
                } else {
                    await this.router.navigateByUrl('/login');
                }
            },
            error => {
                this.router.navigate(['/login']);
            }
        );
    }

    /**
     * Loads the discrepancy choices for the app and save them into the localStorage.
     * Always reloads to avoid stale discrepancy choices (2.4.2 fix).
     */
    private async loadDiscrepancyChoices(): Promise<void> {
        try {
            const response = await lastValueFrom(
                this.discrepanciesService.getDiscrepanciesChoices()
            );
            this.storageService.setItem('discrepancyChoices', response);
        } catch (error) {
            console.warn('Failed to load discrepancy choices:', error);
        }
    }

    /**
     * Finalizes the authentication process by loading discrepancy choices and routing the user.
     * @private
     */
    private async finishAuthenticationProcess(route: string) {
        this.loadDiscrepancyChoices();

        // Navigate to the target route
        try {
            let navigationResult: boolean | undefined;
            if (route === '/') {
                navigationResult = await this.loginService.routeToPage(
                    this.sessionService.getSession()?.termsOfService?.accepted
                );
            } else {
                navigationResult = await this.router.navigateByUrl(route);
            }

            // Try navigating to root as fallback
            if (!navigationResult) {
                await this.router.navigateByUrl('/');
            }
        } catch (navigationError) {
            // Try navigating to root as fallback
            await this.router.navigateByUrl('/');
        }
    }
}
